import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Card } from '../../components/ui/Card';
import { Loader2, Download } from 'lucide-react';
import { exportReportPdf, PdfHeader, PdfKpis, PdfFooter } from './reportPdf';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useAuth } from '../../context/AuthContext';
import { WUNDER_BRAND_ID } from '../../config/brandGroups';
import { MetricsTable, scaleCampaignRows } from './CampaignReport';

// Wunder · Salomon Eylül kampanyaları. Campaign Report'un kardeşi: aynı maliyet
// paneli, kampanya adı sabit "salomon".

const API = 'https://api.glowytics.com';
const CAMPAIGN_LIKE = 'salomon';
const CAMPAIGN_START = new Date(2026, 8, 7);

const formatCurrency = (num) => `₺${Math.round(Number(num || 0)).toLocaleString('en-US')}`;
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const PRESETS = [
  { label: 'Eylül', start: () => new Date(2026, 8, 1), end: () => new Date(2026, 8, 30) },
  { label: 'Kampanya süresi', start: () => CAMPAIGN_START, end: () => new Date() },
];

function aggregate(rows, map) {
  const by = {};
  for (const row of rows || []) {
    const cn = (row.campaign_name || '').trim();
    if (!cn || !cn.toLowerCase().includes(CAMPAIGN_LIKE)) continue;
    if (!by[cn]) by[cn] = { campaignName: cn, impressions: 0, clicks: 0, cost: 0, conversions: 0, revenue: 0, ga4Sessions: 0, ga4Purchases: 0, ga4Revenue: 0 };
    for (const [k, src] of Object.entries(map)) by[cn][k] += Number(row[src] || 0);
  }
  return Object.values(by)
    .filter((x) => x.cost > 0 || x.clicks > 0 || x.impressions > 0)
    .sort((a, b) => b.cost - a.cost);
}

export default function SalomonCampaignReport() {
  const { selectedBrand } = useAuth();
  const brandId = selectedBrand?.id || (typeof window !== 'undefined' ? localStorage.getItem('currentBrandId') : null);
  const [startDate, setStartDate] = useState(() => PRESETS[0].start());
  const [endDate, setEndDate] = useState(() => PRESETS[0].end());
  const [costOverrideGoogle, setCostOverrideGoogle] = useState(null);
  const [costOverrideMeta, setCostOverrideMeta] = useState(null);
  const pdfRef = useRef(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const downloadPdf = async () => {
    setPdfBusy(true);
    try { await exportReportPdf(pdfRef.current, `salomon-${ymd(startDate)}_${ymd(endDate)}.pdf`); } finally { setPdfBusy(false); }
  };
  const [costInputGoogle, setCostInputGoogle] = useState('');
  const [costInputMeta, setCostInputMeta] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleData, setGoogleData] = useState([]);
  const [metaData, setMetaData] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (brandId !== WUNDER_BRAND_ID) return;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const s = ymd(startDate);
        const e = ymd(endDate);
        const hdrs = { credentials: 'include', headers: { 'X-Brand-ID': brandId } };
        const [gRes, mRes] = await Promise.all([
          fetch(`${API}/api/google-ads/unified-campaign-performance?dateRange=custom&startDate=${s}&endDate=${e}`, hdrs).catch(() => null),
          fetch(`${API}/api/google-ads/unified-meta-ga4-performance?dateRange=custom&startDate=${s}&endDate=${e}`, hdrs),
        ]);
        if (!mRes.ok) throw new Error('Failed to fetch');
        setMetaData(aggregate(await mRes.json(), {
          impressions: 'impressions', clicks: 'clicks', cost: 'spend', conversions: 'conversions', revenue: 'conversion_value',
          ga4Sessions: 'ga4_sessions', ga4Purchases: 'ga4_purchases', ga4Revenue: 'ga4_total_revenue',
        }));
        setGoogleData(gRes && gRes.ok ? aggregate(await gRes.json(), {
          impressions: 'google_impressions', clicks: 'google_clicks', cost: 'google_cost', conversions: 'google_conversions', revenue: 'google_conversion_value',
          ga4Sessions: 'ga4_sessions', ga4Purchases: 'ga4_purchases', ga4Revenue: 'ga4_total_revenue',
        }) : []);
      } catch (e) {
        console.error('SalomonCampaignReport fetch:', e);
        setError(e?.message || 'Failed to load');
        setGoogleData([]);
        setMetaData([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [brandId, startDate, endDate]);

  const activePreset = useMemo(
    () => PRESETS.find((p) => ymd(p.start()) === ymd(startDate) && ymd(p.end()) === ymd(endDate))?.label,
    [startDate, endDate]
  );

  const applyCostOverride = () => {
    setCostOverrideGoogle(parseFloat(String(costInputGoogle).replace(/,/g, '.')) || null);
    setCostOverrideMeta(parseFloat(String(costInputMeta).replace(/,/g, '.')) || null);
  };

  const clearCostOverride = () => {
    setCostInputGoogle('');
    setCostInputMeta('');
    setCostOverrideGoogle(null);
    setCostOverrideMeta(null);
  };

  // PDF KPI şeridi: Meta tablosuyla aynı ölçek (override dahil).
  const pdfKpis = useMemo(() => {
    const rows = scaleCampaignRows(metaData, costOverrideMeta, false, null);
    const t = rows.reduce((a, r) => ({ cost: a.cost + (r.cost || 0), revenue: a.revenue + (r.revenue || 0), conversions: a.conversions + (r.conversions || 0) }), { cost: 0, revenue: 0, conversions: 0 });
    return [
      { label: 'Reklam harcaması', value: formatCurrency(t.cost) },
      { label: 'Reklam cirosu', value: formatCurrency(t.revenue) },
      { label: 'Dönüşüm', value: Math.round(t.conversions).toLocaleString('en-US') },
      { label: 'ROAS', value: t.cost > 0 ? `${(t.revenue / t.cost).toFixed(2)}x` : '-', accent: true },
    ];
  }, [metaData, costOverrideMeta]);

  if (!brandId) {
    return (
      <div className="p-6">
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-center">
          <p className="text-amber-800 font-medium">Lütfen bir marka seçin.</p>
        </div>
      </div>
    );
  }

  if (brandId !== WUNDER_BRAND_ID) {
    return (
      <div className="p-6">
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-center">
          <p className="text-amber-800 font-medium">Bu sayfa yalnızca <strong>Wunder</strong> markası için kullanılabilir.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div ref={pdfRef}>
      <PdfHeader brandLogo="/salomon_logo.png" brandName="Salomon" title="Salomon · Eylül Kampanyaları" subtitle="Meta Ads reklam performansı" startDate={startDate} endDate={endDate} />
      <div data-pdf-hide className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-800">Salomon · Eylül Kampanyaları</h1>
          <p className="text-sm text-gray-500 mt-1">Reklam performansı</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={downloadPdf}
            disabled={pdfBusy}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-xl border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {pdfBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            PDF
          </button>
          <div className="inline-flex rounded-lg border border-gray-200 p-0.5 text-xs">
            {PRESETS.map((p) => (
              <button
                key={p.label}
                onClick={() => { setStartDate(p.start()); setEndDate(p.end()); }}
                className={`px-3 py-1.5 rounded-md font-medium ${activePreset === p.label ? 'bg-gray-800 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-600">Start</span>
            <DatePicker
              selected={startDate}
              onChange={(d) => { if (d) { setStartDate(d); if (endDate && d > endDate) setEndDate(d); } }}
              maxDate={endDate}
              className="px-3 py-2 border border-gray-200 rounded-xl text-sm w-32"
              dateFormat="dd.MM.yyyy"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-600">End</span>
            <DatePicker
              selected={endDate}
              onChange={(d) => d && setEndDate(d)}
              minDate={startDate}
              className="px-3 py-2 border border-gray-200 rounded-xl text-sm w-32"
              dateFormat="dd.MM.yyyy"
            />
          </div>
        </div>
      </div>

      <Card data-pdf-card className="p-6 rounded-3xl bg-white border border-gray-200/80 shadow-sm">
        <PdfKpis items={pdfKpis} />
        {error && <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm">{error}</div>}

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          </div>
        ) : (
          <>
            <MetricsTable
              title="Meta Ads"
              icon="/6033716.png"
              alt="Meta Ads"
              data={metaData}
              formatCurrency={formatCurrency}
              costOverride={costOverrideMeta}
              isGoogleAds={false}
              metaAvgBasket={null}
            />
            {googleData.length > 0 && (
              <MetricsTable
                title="Google Ads"
                icon="/google_ads-512.webp"
                alt="Google Ads"
                data={googleData}
                formatCurrency={formatCurrency}
                costOverride={costOverrideGoogle}
                isGoogleAds
              />
            )}
            <p className="text-xs text-gray-400 -mt-4">
              Kampanya adında "salomon" geçen kampanyalar. Revenue = Meta pikselinin atfettiği satış değeri; sepetteki tüm ürünleri kapsar, yalnız Salomon değildir.
            </p>
          </>
        )}

      </Card>
      <PdfFooter />
      </div>

      {/* Cost override panel - fixed bottom right, Glowy Drop düğmesinin solunda */}
      <div
        className="fixed bottom-4 flex flex-col gap-2 p-3 rounded-xl border border-gray-200 bg-white/95 shadow-lg backdrop-blur-sm"
        style={{ zIndex: 50, right: 208 }}
      >
        {(costOverrideGoogle != null || costOverrideMeta != null) && (
          <span className="text-[10px] text-amber-600 font-medium">Override active</span>
        )}
        <div className="flex items-center gap-2">
          {googleData.length > 0 && (
            <input
              type="text"
              value={costInputGoogle}
              onChange={(e) => setCostInputGoogle(e.target.value)}
              placeholder="Google cost"
              className="w-24 px-2 py-1.5 text-sm border border-gray-200 rounded-lg"
            />
          )}
          <input
            type="text"
            value={costInputMeta}
            onChange={(e) => setCostInputMeta(e.target.value)}
            placeholder="Meta cost"
            className="w-24 px-2 py-1.5 text-sm border border-gray-200 rounded-lg"
          />
        </div>
        <div className="flex gap-2">
          <button onClick={applyCostOverride} className="px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            Apply
          </button>
          <button onClick={clearCostOverride} className="px-3 py-1.5 text-xs font-medium bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">
            Reset
          </button>
        </div>
      </div>
    </div>
  );
}
